import { computed } from 'vue';
import type { ComputedRef } from 'vue';
import { useState } from '#app/composables/state';
import type { FavoriteCountResponse } from '~~/server/api/kiwi_user/recipes/favorite_count.post';
import { FAVORITE_RECIPE_KEY } from '~/constants/localStorageKeys';

export const MAX_FAVORITE_RECIPES = 100;

/**
 * お気に入り機能をlocalStorageで管理するcomposable
 */
export function useFavorite() {
  const favoriteIds = useState<number[]>('favoriteRecipeIds', () => []);

  /**
   * 配列が最大件数を超えている場合、新しい方から最大件数分に切り詰める
   */
  function trimToMaxLimit(ids: number[]): number[] {
    if (ids.length <= MAX_FAVORITE_RECIPES) {
      return ids;
    }
    return ids.slice(-MAX_FAVORITE_RECIPES);
  }

  /**
   * localStorageからお気に入りIDを読み込む
   * 最大件数を超えている場合は新しい方から正規化する
   */
  function loadFavorites(): void {
    try {
      const storedValue = localStorage.getItem(FAVORITE_RECIPE_KEY);
      if (storedValue) {
        const parsed = JSON.parse(storedValue);
        if (Array.isArray(parsed)) {
          const filtered = parsed.filter(
            (item): item is number => typeof item === 'number',
          );
          favoriteIds.value = trimToMaxLimit(filtered);
        }
      }
    } catch {
      // NOTE: パースエラーの場合は何もしない
    }
  }

  /**
   * localStorageにお気に入りIDを保存する
   */
  function saveFavorites(): void {
    // NOTE: クライアントサイドでのみ実行
    if (!import.meta.client) {
      return;
    }

    try {
      localStorage.setItem(
        FAVORITE_RECIPE_KEY,
        JSON.stringify(favoriteIds.value),
      );
    } catch {
      // NOTE: 保存エラーの場合は何もしない
    }
  }

  /**
   * お気に入りを追加する
   * 最大件数を超える場合は一番古いものが削除される
   * @param id レシピID
   * @returns 更新後のお気に入り数（エラー時はnull）
   */
  async function addFavorite(id: number): Promise<number | null> {
    // NOTE: 既にお気に入り済みの場合は何もしない
    if (favoriteIds.value.includes(id)) {
      return null;
    }

    // NOTE: localStorageに追加し、超過時は古い方から削除（新しい方から最大100件を保持）
    favoriteIds.value = trimToMaxLimit([...favoriteIds.value, id]);
    saveFavorites();

    try {
      const response = await $fetch<FavoriteCountResponse>(
        '/api/kiwi_user/recipes/favorite_count',
        {
          method: 'POST',
          body: {
            recipe_id: id,
            action: 'add',
          },
        },
      );
      return response.favored_count;
    } catch {
      // NOTE: APIエラー時もlocalStorageの状態は維持（ユーザー体験優先）
      return null;
    }
  }

  /**
   * お気に入りを削除する
   * @param id レシピID
   * @returns 更新後のお気に入り数（エラー時はnull）
   */
  async function removeFavorite(id: number): Promise<number | null> {
    // NOTE: お気に入りに存在しない場合は何もしない
    if (!favoriteIds.value.includes(id)) {
      return null;
    }

    // NOTE: localStorageから削除
    favoriteIds.value = favoriteIds.value.filter((storedId) => storedId !== id);
    saveFavorites();

    try {
      const response = await $fetch<FavoriteCountResponse>(
        '/api/kiwi_user/recipes/favorite_count',
        {
          method: 'POST',
          body: {
            recipe_id: id,
            action: 'remove',
          },
        },
      );
      return response.favored_count;
    } catch {
      // NOTE: APIエラー時もlocalStorageの状態は維持（ユーザー体験優先）
      return null;
    }
  }

  /**
   * 指定したIDがお気に入り済みかどうかを判定する
   * @param id レシピID
   * @returns お気に入り済みかどうか（リアクティブ）
   */
  function isFavorited(id: number): ComputedRef<boolean> {
    return computed(() => favoriteIds.value.includes(id));
  }

  return {
    favoriteIds,
    loadFavorites,
    addFavorite,
    removeFavorite,
    isFavorited,
  };
}
